"use client"

import type React from "react"
import type { BooruPost } from "@/lib/booru-api"
import { useTranslations } from "next-intl"
import { isVRPost } from "@/lib/vr-utils"
import { useFavoritesStore } from "@/lib/favorites-store"
import { useSettingsStore } from "@/lib/settings-store"
import { useResponsiveGridColumns } from "@/hooks/use-responsive-grid-columns"
import { useAutoGridColumns } from "@/hooks/use-auto-grid-columns"
import { cn } from "@/lib/utils"
import { Volume2 } from "@/lib/icons"
import { getBestImageUrl } from "@/lib/image-utils"
import { useBooruCredentialsStore } from "@/lib/booru-credentials-store"
import { useSourceStore } from "@/lib/source-store"
import { SaveToGoonfolioDialog } from "@/components/goonfolio/SaveToGoonfolioDialog"
import { BooruPostCard } from "@/components/booru-post-card"
import { useState, useMemo, useEffect, useCallback, useRef } from "react"
import { useVirtualizer } from "@tanstack/react-virtual"
import { InlineGridAd, RotatingInlineGridAd, injectBookendAds, type InlineAd } from "@/components/ads/inline-grid-ad"
import { InFeedVideoAd, type InFeedVideoAdData } from "@/components/ads/InFeedVideoAd"
import { NetworkDisplayAd } from "@/components/ads/NetworkDisplayAd"
import { useAdStore } from "@/lib/ad-store"
import { warmVideoPreroll } from "@/lib/ad-preroll-cache"
import { ScriptPromoCard, type ScriptPromoAd } from "@/components/marketplace/script-promo-card"
import { CommissionPromoCard, type CommissionPromoAd } from "@/components/marketplace/commission-promo-card"
import { ScriptTrialModal } from "@/components/marketplace/script-trial-modal"
import { ScriptPurchaseModal } from "@/components/marketplace/script-purchase-modal"
import { useAdFrequencyStore } from "@/lib/ad-frequency-store"
import { isAdTestModeOn } from "@/lib/ad-cooldown"
import { isGlobalAdsOff, readGlobalAdsOffCache } from "@/lib/global-ads-off"
import { useAdTier, setModalAdTier, getModalAdTier } from "@/lib/ad-tier"
import { useGooncaveStore } from "@/lib/gooncave-store"
import { useScriptLinkingStore } from "@/lib/script-linking-store"
import { toast } from "sonner"
import { HitomiTagTicker } from "@/components/hitomi-tag-ticker"
import { TitleTicker } from "@/components/title-ticker"
import { CombinedInfoTicker } from "@/components/combined-info-ticker"
import { useDoujinsMetadata, useGalleryScreen } from "@/hooks/use-doujins-metadata"
import { useEromeMetadata } from "@/hooks/use-erome-metadata"
import { DoujinsPremiumModal } from "@/components/doujins-premium-modal"
import { contentTypeDetector } from "@/lib/content-type-detector"
import { AudioCard } from "@/components/audio-card"
import { usePostScriptCounts } from "@/hooks/use-post-script-counts"
import { inferOrderMediaType } from "@/lib/order-media"
import { whenIdle } from "@/lib/when-idle"
import { peekNetworkAdPool, loadNetworkAdPool, type NetworkAdPoolEntry } from "@/lib/network-ad-pool"

interface BooruGridProps {
  posts: BooruPost[]
  onPostClick?: (post: BooruPost) => void
  onTagClick?: (tag: string) => void
  className?: string
  showAds?: boolean
  adFrequency?: number
  customAds?: InlineAd[]
  showScriptPromos?: boolean
  scriptPromoFrequency?: number
  isAlbumView?: boolean
  parentAlbum?: BooruPost
  gridColumnsOverride?: number
  /** Scroll container element — enables row virtualization for the grid layout */
  scrollContainerRef?: React.RefObject<HTMLElement | null>
  /**
   * @deprecated No longer read. Masonry now distributes items into explicit
   * flex columns (see the masonry branch in this file), which is inherently
   * append-stable and seam-free, so per-page batching is unnecessary. Kept
   * on the interface only so existing callers don't break; safe to remove
   * once they stop passing it.
   */
  infiniteScrollBatchBoundaries?: number[]
}

// Tags of what's actually on screen right now, so tag-targeted ad zones and
// creator promos can match. Contextual (current page content), not personal
// data, so it's fine to send regardless of ad-consent — distinct from the
// opt-in behavioral affinity tags the serve routes merge in server-side for
// signed-in, consented users. Extracted out of fetchScriptPromos (which
// already computed this for creator promo targeting) so the same real
// content signal also drives network-zone category targeting — see the
// 19 category-targeted grid zones (creative_tier 3, notes `category:*`)
// added earlier this session, which sat inactive with no selection logic.
function computeContextTags(posts: BooruPost[]): string[] {
  const counts = new Map<string, number>()
  for (const post of posts.slice(0, 30)) {
    for (const tag of post.tags || []) {
      counts.set(tag, (counts.get(tag) || 0) + 1)
    }
  }
  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 15)
    .map(([tag]) => tag)
}

// Minimum number of real posts that must separate any two kept ad/promo
// slots in the unified injection pass below (postsWithAdsAndPromos), no
// matter which of the six ad/promo families each one belongs to. Before this
// existed, six independent passes each computed "every Nth post" against
// whatever array the PRIOR passes had already spliced ad slots into, so a
// later pass's index space silently included earlier passes' ad cards —
// nothing stopped two different ad types from landing one or two cards apart
// in the rendered grid. Candidates are now computed independently per type
// against the original, un-injected post indices, then merged and thinned
// by this gap so kept slots (of any type) are always at least this many real
// posts apart. Not wired to the admin UI yet — that's a deliberate, separate
// future step.
// Late-arriving ad data (house ads, promos) may only add a slot at or past this many posts: earlier slots are
// frozen once placed so nothing is inserted into rows the visitor is already looking at.
const LATE_SLOT_MIN_INDEX = 12
const MIN_GAP_BETWEEN_ADS = 8

const NO_NETWORK_ADS: NetworkAdPoolEntry[] = []

// An empty box with the shape of a typical grid ad. Rendered where a network ad WILL go while the ad pool is
// still loading, so the grid is laid out with the slot already in place (see networkPoolState in BooruGrid).
function AdSlotPlaceholder({ height }: { width?: number | null; height?: number | null }) {
  // A FIXED pixel height, not an aspect ratio: real grid ads render at their native size (300px wide,
  // centered), they are not stretched to the column width, so an aspect-ratio box was far too tall.
  const h = height && height > 0 ? height : 250
  return <div aria-hidden className="w-full rounded-xl bg-muted/30" style={{ height: h }} />
}

export function BooruGrid({
  posts: allPosts,
  onPostClick,
  onTagClick,
  className,
  showAds = true,
  adFrequency = 12, // This is now a fallback, will use dynamic config
  customAds,
  showScriptPromos = true,
  scriptPromoFrequency = 15, // This is now a fallback, will use dynamic config
  isAlbumView = false,
  parentAlbum,
  gridColumnsOverride,
  scrollContainerRef,
  infiniteScrollBatchBoundaries,
}: BooruGridProps) {
  const t = useTranslations("booruGrid")
  // Doujins and HentaiEra cards carry no real tags, so Platform tags cannot judge them from the list: each gallery's tags are looked up
  // and a card appears only after it passed (a blocked one never does). Every other source passes straight through.
  const posts = useGalleryScreen(allPosts)
  const { toggleFavorite, isFavorite } = useFavoritesStore()
  const { getCredentials } = useBooruCredentialsStore()
  const { gridColumns: defaultGridColumns } = useResponsiveGridColumns()
  const { gridGap, gridLayout: globalGridLayout, gridFitShape, gridFitMode, gridFitBlur, gridFlowDirection, borderRadius, gridThumbnailQuality, gridColumnsAuto } = useSettingsStore()
  const { isPremiumUser } = useAdStore()
  const adTier = useAdTier(posts)
  if (posts.length > 0) setModalAdTier(adTier) // corner widgets without posts follow the browse tier
  // "Force Ads" test mode's whole documented purpose (AdTestPanel.tsx) is
  // bypassing every frequency/cooldown check — but premium/ad-free status
  // was never one of the things it bypassed, so a premium subscriber's own
  // account (any real Goontier, not just admin) could never see an ad
  // render at all, test mode on or not. Reported live testing the new album
  // bookend ads on a goon_max account. This is the one flag that should
  // still win over test mode: a genuinely premium viewer should never see
  // real ads outside this deliberate self-test.
  const effectivePremiumUser = (isPremiumUser || isGlobalAdsOff()) && !isAdTestModeOn()

  // Check if we're displaying categories (all posts are categories)
  const isShowingCategories = posts.length > 0 && posts.every(post => post.rawPost?.isCategory === true)
  
  // Override grid layout for XlecX: default to square grid
  const isXlecx = posts.length > 0 && posts[0]?.site === 'xlecx'
  // *** AUDIOCHAN IS EXEMPT FROM GRID CHANGES (owner order 2026-10-09; see CLAUDE.md). It has its own layout: one wide horizontal row per
  // audio, centred, never masonry or multi-column. Do not change the isAudioGrid branches below when working on the picture grid. ***
  // Audio results (AudioChan) are text-heavy cards, not pictures: masonry columns of picture width squeezed the titles to one word per
  // line. They get one wide, horizontal row per audio instead (room for the title, the tags, and the hover toolbar), centred.
  const isAudioGrid = posts.length > 0 && posts.every((post) => post.type === 'audio')
  const gridLayout = isXlecx || isAudioGrid ? 'square' as const : globalGridLayout
  // Fitted layout: fixed-shape cells (not for category tiles, which have their own look).
  const fitted = gridLayout === "fitted" && !isShowingCategories
    ? { ratio: { landscape: "4 / 3", portrait: "3 / 4", square: "1 / 1" }[gridFitShape], fit: gridFitMode, blur: gridFitBlur }
    : undefined
  
  // Override grid columns for categories: 3 columns on desktop, 2 on tablet, 1 on mobile
  const hasGridColumnsOverride = typeof gridColumnsOverride === "number" && gridColumnsOverride > 0
  // Auto (Settings > Grid): the count follows the width of the area the grid scrolls in, for every layout (masonry, square, fitted, ...).
  // Fixed picks, category tiles and AudioChan's single row (exempt, see above) keep their own counts.
  const autoGridColumns = useAutoGridColumns(scrollContainerRef, gridGap, gridColumnsAuto)
  const gridColumns = isShowingCategories ? 3 : hasGridColumnsOverride ? gridColumnsOverride : isAudioGrid ? 1 : gridColumnsAuto ? autoGridColumns : defaultGridColumns

  const { config, fetchConfig, getFrequency, isEnabled, getMaxSlots, getCycleSeconds } = useAdFrequencyStore()

  const [collectionPost, setCollectionPost] = useState<BooruPost | null>(null)
  const [longPressTimer, setLongPressTimer] = useState<NodeJS.Timeout | null>(null)
  const [longPressTriggered, setLongPressTriggered] = useState(false)
  const [dismissedAds, setDismissedAds] = useState<Set<string>>(new Set())
  // Masonry column pinning — see the assignment block in the masonry branch
  // below. Maps an item's stable identity to the column it was first placed
  // in, so late-resolving data (Erome 0x0 placeholders, the async grid ad
  // pool) can never relocate something the user is already looking at.
  // Cleared only when the column count itself changes.
  const masonryAssignmentsRef = useRef<Map<string, number>>(new Map())
  const masonryColumnCountRef = useRef<number>(0)
  // Ad slots already placed (see "Stable placement" in postsWithAdsAndPromos).
  const stableSlotsRef = useRef<{ sig: string; len: number; kept: Map<number, { postIndex: number; priority: number; item: any }> }>({ sig: "", len: 0, kept: new Map() })
  // The network ad pool is fetched ONCE per ad tier for the life of this grid. This effect re-runs (dev
  // StrictMode, remounts of the settings it depends on) and every run used to fetch a fresh pool of 6
  // and REPLACE the published one: measured ~20 fetches in one page load. Each replacement swapped the
  // creative (and its size: 300x250 vs 300x500) in every ad slot already on screen, shifting the
  // columns below it. Once a pool for this tier has been requested, later runs leave it alone.
  const networkPoolTierRef = useRef<number | string | null>(null)
  const [scriptPromos, setScriptPromos] = useState<ScriptPromoAd[]>([])
  const [dismissedPromos, setDismissedPromos] = useState<Set<string>>(new Set())
  const [commissionPromos, setCommissionPromos] = useState<CommissionPromoAd[]>([])
  const [dismissedCommissionPromos, setDismissedCommissionPromos] = useState<Set<string>>(new Set())
  const [trialPromo, setTrialPromo] = useState<ScriptPromoAd | null>(null)
  const [purchasePromo, setPurchasePromo] = useState<ScriptPromoAd | null>(null)
  const [showPremiumModal, setShowPremiumModal] = useState(false)
  // Network display ad — a POOL of distinct zone results, not one shared
  // value. This used to be a single fetch reused for every network_ad slot
  // in the grid: fine when there's at most one slot (real-world cadence is
  // 1 per 30 posts), but Force Ads test mode drops that to 1 per 3 posts,
  // and every one of those slots rendered the literal same iframe src —
  // reported live as one specific creative (an unrelated advertiser's,
  // "candy.ai") tiling the entire grid dozens of times over. Real cadence
  // is sparse enough this was never visible in production, but the
  // underlying bug — N slots sharing 1 fetch — was there regardless of
  // test mode; a real page with enough posts to earn 2+ network ad slots
  // would hit the exact same duplicate-creative result. Each pool entry
  // carries its own creative_width/creative_height (see the ad_zones
  // migration referenced on NetworkDisplayAd) and its own ad_zones UUID for
  // impression reporting — NOT ExoClick's numeric id.
  // The pool is stored WITH the tier it was fetched for and only used while that tier is still the current one.
  // The tier is computed from the posts on screen and is 3 (explicit) until there are any, so a pool fetched at
  // mount for a safe-only source (Safebooru, Sakugabooru) was an EXPLICIT-tier pool that could be shown on that
  // grid until the tier-1 pool replaced it, and cost 6 wasted requests.
  const [loadedNetworkPool, setLoadedNetworkPool] = useState<{ tier: number | string; entries: NetworkAdPoolEntry[]; settled: "ready" | "empty" } | null>(null)
  const hasPostsForAds = posts.length > 0
  // A recent pool for this tier is reused straight away, so ad slots get their REAL size on the first paint.
  const cachedNetworkPool = hasPostsForAds ? peekNetworkAdPool(adTier) : null
  const networkAdPool: NetworkAdPoolEntry[] =
    loadedNetworkPool && loadedNetworkPool.tier === adTier && loadedNetworkPool.entries.length > 0
      ? loadedNetworkPool.entries
      : cachedNetworkPool?.entries ?? NO_NETWORK_ADS
  // "loading" until the first pool request round settles. While loading, ad slots are RESERVED (an empty box
  // of the usual size) instead of absent: inserting a 500px ad into a column that is already on screen, when
  // the pool arrives, pushed everything below it down (measured: an "Ad" 500px tall appearing at the top of
  // column 2, then resizing to 250px). With the slot present from the first paint the pool only fills a box.
  const networkPoolState: "loading" | "ready" | "empty" =
    networkAdPool.length > 0 ? "ready" : loadedNetworkPool && loadedNetworkPool.tier === adTier ? loadedNetworkPool.settled : "loading"
  const [networkVideoUrl, setNetworkVideoUrl] = useState<string | null>(null)
  // Outstream Video zone (ExoClick "Outstream Video") — InFeedVideoAd already
  // had a fully built embed_js render path (autoplay-on-view via
  // IntersectionObserver, sandboxed iframe) with a "video_ad" item type
  // wired into both render layouts below, but nothing ever injected one:
  // no fetch, no cadence logic. That's this and the block further down.
  const [outstreamVideoJs, setOutstreamVideoJs] = useState<string | null>(null)
  const [outstreamZoneId, setOutstreamZoneId] = useState<string | null>(null)
  // House ads actually configured in the admin panel (/api/ads/serve reads the
  // house_ads table). Without this fetch, `customAds` is never passed by any
  // caller, so every in-grid house ad fell back to the hardcoded MOCK_INLINE_ADS
  // array — meaning admin edits/toggles/new ads never appeared anywhere.
  const [liveHouseAds, setLiveHouseAds] = useState<InlineAd[] | null>(null)
  // Affiliate ads (house_ads.is_affiliate) — a fully separate rotation from
  // the default house-ad pool above, fetched with its own ?affiliate=1 query
  // so it never dilutes/duplicates into inline_sponsored_ads. See the
  // affiliate_ads cadence block in postsWithAdsAndPromos below.
  const [liveAffiliateAds, setLiveAffiliateAds] = useState<InlineAd[]>([])
  useEffect(() => {
    // forceAds=1 lets a premium account (e.g. this app's own team testing on
    // a real subscriber account) see house ads via the same "Force Ads" test
    // flag as everything else — see the matching comment in
    // app/api/ads/serve/route.ts for why trusting it here is safe.
    const forceAds = isAdTestModeOn() ? "&forceAds=1" : ""
    fetch(`/api/ads/serve?count=10${forceAds}`)
      .then((r) => r.json())
      .then((ads) => { if (Array.isArray(ads) && ads.length > 0) setLiveHouseAds(ads) })
      .catch(() => {})
    fetch(`/api/ads/serve?count=10&affiliate=1${forceAds}`)
      .then((r) => r.json())
      .then((ads) => { if (Array.isArray(ads)) setLiveAffiliateAds(ads) })
      .catch(() => {})
  }, [])

  // Doujins metadata fetching
  const { observePost, unobservePost, getMetadata, clearObservedPosts } = useDoujinsMetadata()
  // Erome lazy-placeholder metadata fetching (same viewport-triggered/throttled pattern)
  const { observeErome, getEromeMetadata } = useEromeMetadata()
  const { getCount: getScriptCount, getTopScript } = usePostScriptCounts(posts)
  
  // Clear observed posts when the posts array changes (e.g., navigating to a new category)
  useEffect(() => {
    if (posts.length > 0 && posts[0]?.site === 'doujins') {
      clearObservedPosts()
    }
  }, [posts, clearObservedPosts])

  // Get bearer tokens for Sankaku sites (only if enabled)
  const { sources } = useSourceStore()
  const sankakuEnabled = sources.find(s => s.id === 'sankaku')?.enabled
  const idolSankakuEnabled = sources.find(s => s.id === 'idolsankaku')?.enabled
  const sankakuToken = sankakuEnabled ? getCredentials('sankaku')?.apiKey : undefined
  const idolSankakuToken = idolSankakuEnabled ? getCredentials('idolsankaku')?.apiKey : undefined

  useEffect(() => {
    // Creator promos (script promos + commission/store/game promos) are real
    // creator content, "part of the app itself" — not suppressed for a real
    // premium member, unlike network/house ads. Was gated on
    // !effectivePremiumUser, which meant a paying member's browser never
    // even fetched this data.
    if (showScriptPromos) {
      fetchScriptPromos()
      fetchCommissionPromos()
    }
    fetchConfig()
    // Fetch active network ad codes for the grid — a small POOL of
    // independent calls, not one shared fetch. /api/ads/network-grid-ad
    // already picks randomly among up to 20 matching zones per call, so N
    // parallel calls naturally surface N different creatives (deduped by
    // zone below) instead of every grid slot cloning the same one.
    if (!effectivePremiumUser) {
      const contextTags = computeContextTags(posts)
      const tagsParam = contextTags.length > 0 ? `&tags=${encodeURIComponent(contextTags.join(","))}` : ""
      // Only once there are posts: before that the tier is the explicit default, not the real one.
      if (hasPostsForAds && networkPoolTierRef.current !== adTier) {
        networkPoolTierRef.current = adTier
        const requestedTier = adTier
        // One shared, cached pool per tier (lib/network-ad-pool.ts), however many grids mount.
        loadNetworkAdPool(requestedTier, tagsParam).then((pool) => {
          if (pool.videoUrl) setNetworkVideoUrl(pool.videoUrl)
          setLoadedNetworkPool({ tier: requestedTier, entries: pool.entries, settled: pool.entries.length > 0 ? "ready" : "empty" })
        })
      }
      // Same rule as the pool: wait for posts so the tier is the real one, not the explicit-tier default.
      if (hasPostsForAds) setTimeout(() => whenIdle(() => {
        fetch(`/api/ads/network-grid-ad?type=outstream_video&tier=${adTier}${tagsParam}`)
          .then((r) => r.ok ? r.json() : null)
          .then((d) => {
            if (d?.display_js) setOutstreamVideoJs(d.display_js)
            if (d?.zone_id) setOutstreamZoneId(d.zone_id)
          })
          .catch(() => {})
      }, 1500), 1200)
      // Resolve the next video preroll ad now, while the user is still
      // browsing the grid — by the time they actually open a video, the
      // network round-trip + VAST parse is already done instead of
      // happening (visibly, as a "Loading ad..." flash) after the click.
      setTimeout(() => whenIdle(() => warmVideoPreroll(getModalAdTier()), 3000), 2500)
    }
  }, [showScriptPromos, effectivePremiumUser, fetchConfig, adTier, hasPostsForAds])

  const fetchScriptPromos = async () => {
    try {
      // Two placements feed this grid, both now served from creator_promotions:
      // the flat-daily "marketplace_inline" placement (via /api/marketplace/featured,
      // scripts-only) and the budget-or-flat "main_feed" placement (via
      // /api/marketplace/ads/serve). Kept as two fetches/two response shapes
      // — inline slots share a response with the two other marketplace
      // placements, main_feed promos share a response with every entity type
      // that can buy it — merged client-side so neither placement gets
      // systematically buried when both have active promos.
      //
      const contextTags = computeContextTags(posts)
      const tagsParam = contextTags.length > 0 ? `&tags=${encodeURIComponent(contextTags.join(","))}` : ""

      const [featuredRes, campaignsRes] = await Promise.all([
        fetch("/api/marketplace/featured"),
        fetch(`/api/marketplace/ads/serve?limit=3${tagsParam}`),
      ])

      const slotAds: ScriptPromoAd[] = []
      if (featuredRes.ok) {
        const data = await featuredRes.json()
        for (const slot of data.inline || []) {
          slotAds.push({
            campaign_id: slot.slot_id,
            script_id: slot.id,
            title: slot.title,
            description: slot.description,
            thumbnail_url: slot.thumbnail_url,
            thumbnail_url_square: slot.thumbnail_url_square,
            preview_video_url: slot.preview_video_url,
            price_cents: slot.price_cents,
            trial_duration_seconds: slot.trial_duration_seconds,
            avg_rating: slot.avg_rating,
            purchase_count: slot.purchase_count,
            tags: slot.tags,
            creator_username: slot.creator_username,
            creator_avatar: slot.creator_avatar,
            hide_text_overlay: !!slot.hide_text_overlay,
            hide_badge: !!slot.hide_badge,
            hide_dim_overlay: !!slot.hide_dim_overlay,
            is_promo: true as const,
          })
        }
      }

      const campaignAds: ScriptPromoAd[] = campaignsRes.ok ? (await campaignsRes.json()).ads || [] : []

      // Interleave rather than one-block-then-the-other so neither system
      // gets systematically buried when both have active promos.
      const merged: ScriptPromoAd[] = []
      const max = Math.max(slotAds.length, campaignAds.length)
      for (let i = 0; i < max; i++) {
        if (slotAds[i]) merged.push(slotAds[i])
        if (campaignAds[i]) merged.push(campaignAds[i])
      }
      setScriptPromos(merged.slice(0, 3))
    } catch (error) {
      console.error("Failed to fetch script promos:", error)
    }
  }

  const fetchCommissionPromos = async () => {
    try {
      const contextTagCounts = new Map<string, number>()
      for (const post of posts.slice(0, 30)) {
        for (const tag of post.tags || []) {
          contextTagCounts.set(tag, (contextTagCounts.get(tag) || 0) + 1)
        }
      }
      const contextTags = Array.from(contextTagCounts.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 15)
        .map(([tag]) => tag)
      const tagsParam = contextTags.length > 0 ? `&tags=${encodeURIComponent(contextTags.join(","))}` : ""

      const res = await fetch(`/api/commissions/ad-promotions/serve?placement=main_feed&limit=2${tagsParam}`)
      if (res.ok) {
        const data = await res.json()
        setCommissionPromos(data.promotions || [])
      }
    } catch (error) {
      console.error("Failed to fetch commission promos:", error)
    }
  }

  const adTestMode = isAdTestModeOn()
  // Reading an opened album/doujin straight through is a continuous flow, so
  // it gets its own frequency + enable switch (album_grid_ads) instead of
  // inheriting the browse grid's much denser inline_sponsored_ads cadence —
  // the same every-10 that reads fine while scanning a grid is intrusive
  // mid-album. This same BooruGrid renders both surfaces (AlbumGridView
  // passes isAlbumView), which is why they shared one knob until now.
  const adPlacementKey = isAlbumView ? "album_grid_ads" : "inline_sponsored_ads"
  const dynamicAdFrequency = adTestMode
    ? 1
    : (config ? getFrequency(adPlacementKey) : adFrequency)
  // Only meaningful for the browse grid, not albums — albums already cap at
  // 2 (one opening + one closing bookend) by construction regardless.
  // Deliberately uncapped by default: a real slot number here used to mean
  // "this many ads, ever, for the whole session" (injectAdsIntoPosts always
  // re-scans from post #1, so the first N eligible spots near the top were
  // the ONLY ones that ever fired — everything past them stayed silent no
  // matter how far the user kept scrolling). The actual intent was a
  // recurring cadence for the whole session, not a lifetime total, so the
  // fallback here now matches injectAdsIntoPosts' own uncapped default
  // instead of silently reintroducing a cap.
  const dynamicAdMaxSlots = adTestMode ? Infinity : (config ? getMaxSlots("inline_sponsored_ads") ?? Infinity : Infinity)
  const dynamicAdCycleSeconds = config ? getCycleSeconds("inline_sponsored_ads") : 0
  const dynamicScriptPromoFrequency = config ? getFrequency("creator_script_promos") : scriptPromoFrequency
  const showAdsEnabled = config ? isEnabled(adPlacementKey) : showAds
  const showScriptPromosEnabled = config ? isEnabled("creator_script_promos") : showScriptPromos
  // Independent switch — commission_store_promos previously rode
  // creator_script_promos' enable flag even though it's a different product
  // (artists promoting their storefront, not scriptors promoting a script),
  // so disabling script promos silently disabled paid commission promotions
  // too. Still shares the rotation slot/rhythm above on purpose.
  const showCommissionPromosEnabled = config ? isEnabled("commission_store_promos") : showScriptPromos

  const postsWithAdsAndPromos = useMemo(() => {
    let items: Array<
      | { type: "post"; post: BooruPost }
      | { type: "ad"; ad: InlineAd; pool: InlineAd[] }
      | { type: "script_promo"; promo: ScriptPromoAd }
      | { type: "commission_promo"; promo: CommissionPromoAd }
      | { type: "network_ad"; embedJs: string; label?: string; zoneId: string | null; zoneWidth: number | null; zoneHeight: number | null }
      | { type: "video_ad"; ad: InFeedVideoAdData }
    > = posts.map((p) => ({ type: "post" as const, post: p }))

    // An album is bookended (one ad up front, one after the last page for a
    // long enough album) rather than cadenced, so it has no minimum-length
    // gate — a 3-page album still gets its single opening ad.
    const meetsAdMinimum = isAlbumView || posts.length >= dynamicAdFrequency
    const isAudioOnly = posts.every((p) => p.type === "audio")

    if (isAlbumView) {
      // Album view is bookended, not cadenced, and is explicitly exempted
      // from the unified multi-type merge below — it only ever gets this one
      // house-ad pass, capped at two slots total (opening + closing) by
      // injectBookendAds itself. Do not touch this branch for the ad-spacing
      // fix — see MIN_GAP_BETWEEN_ADS's comment above.
      //
      // NOT premium-gated here anymore — house_ads is a mix of general self-
      // promo (bounties/goonpals/commissions/discord/scripting — fine to show
      // anyone) and exactly one membership upsell (house_membership). The
      // server (/api/ads/serve) already excludes is_upgrade_promo rows for a
      // real premium member; InlineGridAd's own render gate excludes it again
      // for an active Skip-Ads grant. Blocking injection here entirely used to
      // suppress the general self-promo ads too, which have nothing to do
      // with selling a membership. See migration
      // 20261211000000_house_ads_upgrade_promo_flag.sql.
      if (showAdsEnabled && meetsAdMinimum && !isAudioOnly) {
        // No MOCK_INLINE_ADS fallback here on purpose. /api/ads/serve used to
        // return [] outright for premium/ad-free viewers, which left
        // liveHouseAds null and silently fell through to the hardcoded demo
        // array — rendering ads with baked-in title/subtitle/badge text that
        // no longer exists in house_ads at all (every row's title had been
        // cleared in admin for image-only creatives). Result, reported live:
        // the grid showed the creative's own artwork PLUS a stale hardcoded
        // headline over it, and no admin edit could remove it. Real ads or
        // nothing.
        const ads = (customAds || liveHouseAds || []).filter((ad) => !dismissedAds.has(ad.id))
        items = injectBookendAds(items as any, ads, dynamicAdFrequency) as typeof items
      }
    } else {
      // Unified injection pass. Six ad/promo families (house, script promo,
      // commission promo, affiliate, outstream video, network display) each
      // compute their own candidate insertion positions independently
      // against the ORIGINAL `posts` array indices — exactly the same
      // `(index + 1) % frequency === 0` math each pass always used, just no
      // longer counting other families' already-inserted ad cards as part of
      // the index space. All candidates are then merged: same-postIndex ties
      // are resolved by priority (house > script promo > commission promo >
      // affiliate > outstream video > network display), and the survivors
      // are walked in postIndex order, keeping one only if it's at least
      // MIN_GAP_BETWEEN_ADS real posts past the last KEPT ad of any type.
      // A candidate that loses a tie or fails the gap check is dropped
      // outright, not rescheduled — that family's next natural multiple
      // further down the feed gets its own independent chance.
      type AdItem = Exclude<(typeof items)[number], { type: "post" }>
      type AdCandidate = { postIndex: number; priority: number; item: AdItem }
      const candidates: AdCandidate[] = []

      // --- 1. House ads (inline_sponsored_ads) — priority 0, highest ---
      // NOT premium-gated here anymore — house_ads is a mix of general self-
      // promo (bounties/goonpals/commissions/discord/scripting — fine to show
      // anyone) and exactly one membership upsell (house_membership). The
      // server (/api/ads/serve) already excludes is_upgrade_promo rows for a
      // real premium member; InlineGridAd's own render gate excludes it again
      // for an active Skip-Ads grant. Blocking injection here entirely used to
      // suppress the general self-promo ads too, which have nothing to do
      // with selling a membership. See migration
      // 20261211000000_house_ads_upgrade_promo_flag.sql.
      if (showAdsEnabled && meetsAdMinimum && !isAudioOnly) {
        // No MOCK_INLINE_ADS fallback here on purpose — see the comment on
        // this same check in the album branch above.
        const ads = (customAds || liveHouseAds || []).filter((ad) => !dismissedAds.has(ad.id))
        if (ads.length > 0 && dynamicAdFrequency > 0 && dynamicAdMaxSlots > 0) {
          // Mirrors injectAdsIntoPosts' own cap semantics exactly:
          // slotsInserted stops at dynamicAdMaxSlots, AND adIndex < ads.length
          // stops it again once the pool has cycled through once. Both were
          // insertion caps in the old single pass; here they're candidate-
          // generation caps feeding into the shared gap/priority merge below.
          let adIndex = 0
          let slotsInserted = 0
          for (let index = 0; index < posts.length; index++) {
            if (slotsInserted >= dynamicAdMaxSlots || adIndex >= ads.length) break
            if ((index + 1) % dynamicAdFrequency === 0) {
              candidates.push({
                postIndex: index,
                priority: 0,
                item: { type: "ad", ad: ads[adIndex % ads.length], pool: ads },
              })
              adIndex++
              slotsInserted++
            }
          }
        }
      }

      // --- 2. Script promo (creator_script_promos) — priority 1 ---
      // NOT premium-gated — creator promos always show, see the fetch
      // effect's comment above.
      if (showScriptPromosEnabled && scriptPromos.length > 0) {
        const availablePromos = scriptPromos.filter((p) => !dismissedPromos.has(p.campaign_id))
        let promoIndex = 0
        for (let index = 0; index < posts.length; index++) {
          if (promoIndex >= availablePromos.length) break
          if ((index + 1) % dynamicScriptPromoFrequency === 0 && index > dynamicScriptPromoFrequency / 2) {
            candidates.push({
              postIndex: index,
              priority: 1,
              item: { type: "script_promo", promo: availablePromos[promoIndex] },
            })
            promoIndex++
          }
        }
      }

      // --- 3. Commission promo (commission_store_promos) — priority 2 ---
      // Same rotation as script promos, offset by half the frequency so the
      // two promo types don't naturally land back-to-back. Also not
      // premium-gated, same reasoning as script promos above.
      if (showCommissionPromosEnabled && commissionPromos.length > 0) {
        const availableCommissionPromos = commissionPromos.filter((p) => !dismissedCommissionPromos.has(p.promotion_id))
        let commissionPromoIndex = 0
        const offset = Math.floor(dynamicScriptPromoFrequency / 2)
        for (let index = 0; index < posts.length; index++) {
          if (commissionPromoIndex >= availableCommissionPromos.length) break
          if (
            (index + 1 + offset) % dynamicScriptPromoFrequency === 0 &&
            index > dynamicScriptPromoFrequency / 2
          ) {
            candidates.push({
              postIndex: index,
              priority: 2,
              item: { type: "commission_promo", promo: availableCommissionPromos[commissionPromoIndex] },
            })
            commissionPromoIndex++
          }
        }
      }

      // --- 4. Affiliate ads — priority 3 ---
      // Third-party advertising content, its own rotation entirely
      // independent of the inline_sponsored_ads cadence above (liveHouseAds
      // already excludes is_affiliate rows server-side, so there's no
      // overlap/duplication between the two pools). Reuses the same
      // `{ type: "ad", ... }` slot shape as house ads — InlineGridAd itself
      // doesn't need to know which pool a given ad came from. Premium-
      // skipped for the same reasons as outstream/network below: real
      // third-party ad inventory, not in-app self-promo.
      const affiliateAdsEnabled = config ? isEnabled("affiliate_ads") : false
      const affiliateAdFrequency = config ? getFrequency("affiliate_ads") || 75 : 75
      if (!effectivePremiumUser && affiliateAdsEnabled && posts.length >= affiliateAdFrequency) {
        const ads = liveAffiliateAds.filter((ad) => !dismissedAds.has(ad.id))
        if (ads.length > 0) {
          let adIndex = 0
          for (let index = 0; index < posts.length; index++) {
            if ((index + 1) % affiliateAdFrequency === 0) {
              candidates.push({
                postIndex: index,
                priority: 3,
                item: { type: "ad", ad: ads[adIndex % ads.length], pool: ads },
              })
              adIndex++
            }
          }
        }
      }

      // --- 5. Outstream video — priority 4 ---
      // Same "every N×base-frequency, only if a real embed is configured"
      // shape as the network display slot below. InFeedVideoAd's own
      // IntersectionObserver decides when to actually play it (on-view
      // autoplay muted, pause off-view); this only decides where in the
      // feed the slot itself lands.
      const outstreamEnabled = config ? isEnabled("outstream_video_ads") : true
      const outstreamFreqBase = config ? getFrequency("outstream_video_ads") || 12 : 12
      if (!effectivePremiumUser && outstreamEnabled && outstreamVideoJs && posts.length >= outstreamFreqBase) {
        for (let index = 0; index < posts.length; index++) {
          if ((index + 1) % outstreamFreqBase === 0) {
            candidates.push({
              postIndex: index,
              priority: 4,
              item: {
                type: "video_ad",
                ad: {
                  id: `outstream-${outstreamZoneId ?? "zone"}-${index}`,
                  campaign_id: outstreamZoneId ?? "outstream_video",
                  embed_js: outstreamVideoJs,
                  badge_text: "Ad",
                },
              },
            })
          }
        }
      }

      // --- 6. Network display ad — priority 5 ---
      // Own cadence (admin: "Network Display Ads", network_display_ads), NOT 3x the house-ad frequency:
      // house ads are deliberately sparse, and deriving this from them meant one paid ad every 90
      // posts (and none before 90 posts were loaded). Only when a real embed is configured —
      // NetworkDisplayAd renders nothing when embedJs is empty.
      const networkAdsEnabled = config ? isEnabled("network_display_ads") : true
      const networkFreq = Math.max(1, config ? getFrequency("network_display_ads") || 12 : 12)
      // Only hold space for an ad this browser already KNOWS is coming (it has the sitewide flag cached and it is off).
      // A first-time visitor, or anyone while the sitewide Ads OFF switch is on, would otherwise get an empty box
      // that vanishes when the server returns no ad.
      const reservingNetworkSlots = networkAdPool.length === 0 && networkPoolState === "loading" && readGlobalAdsOffCache() === false
      if (!effectivePremiumUser && networkAdsEnabled && (networkAdPool.length > 0 || reservingNetworkSlots) && posts.length >= networkFreq) {
        // Offset so the slots don't sit on the exact same indexes as the promo cadences.
        const networkOffset = Math.floor(networkFreq / 2)
        let poolIndex = 0
        for (let index = 0; index < posts.length; index++) {
          if ((index + 1 + networkOffset) % networkFreq === 0) {
            // Cycle through the pool so consecutive slots don't all render the identical creative.
            const ad: NetworkAdPoolEntry = networkAdPool.length > 0
              ? networkAdPool[poolIndex % networkAdPool.length]
              : { displayJs: "", zoneId: null, zoneWidth: 300, zoneHeight: 250 } // reserved box, no creative yet
            poolIndex++
            candidates.push({
              postIndex: index,
              priority: 5,
              item: {
                type: "network_ad",
                embedJs: ad.displayJs,
                label: "Ad",
                zoneId: ad.zoneId,
                zoneWidth: ad.zoneWidth,
                zoneHeight: ad.zoneHeight,
              },
            })
          }
        }
      }

      // --- Merge: one winner per postIndex (ties broken by priority), then
      // greedily keep winners at least MIN_GAP_BETWEEN_ADS real posts apart.
      //
      // MIN_GAP_BETWEEN_ADS is a count of POSTS, but the grid renders
      // gridColumns posts per row (masonry or not) — a flat post gap doesn't
      // mean a consistent visual gap, it means a SHRINKING one as columns
      // increase. At gridColumns=3, a gap of 8 posts is only ~2.7 rows, so
      // multiple independently-cadenced ad families (house/script/commission/
      // affiliate/video/network) can each land within the same screenful,
      // looking like several ads clustered together even though the flat
      // index math was technically respected. Scaling by column count keeps
      // the gap meaning the same number of ROWS regardless of how many
      // columns are on screen. Reported live: "3 ads together on one page."
      const effectiveMinGap = MIN_GAP_BETWEEN_ADS * Math.max(1, gridColumns)
      const winnerByIndex = new Map<number, AdCandidate>()
      for (const c of candidates) {
        if (c.priority === 5) continue // network ads are placed separately below
        const existing = winnerByIndex.get(c.postIndex)
        if (!existing || c.priority < existing.priority) {
          winnerByIndex.set(c.postIndex, c)
        }
      }
      const orderedWinners = Array.from(winnerByIndex.values()).sort((a, b) => a.postIndex - b.postIndex)
      const keptByIndex = new Map<number, AdCandidate>()
      let lastKeptIndex = -Infinity
      for (const winner of orderedWinners) {
        if (winner.postIndex - lastKeptIndex >= effectiveMinGap) {
          keptByIndex.set(winner.postIndex, winner)
          lastKeptIndex = winner.postIndex
        }
      }
      // Network (paid) ads used to go through the same greedy pass as the promos, lowest priority and
      // last, so any promo within ~8 rows knocked them out. They now only have to stay a short gap
      // (2 rows) away from any kept ad, so promos can no longer crowd them out of the feed.
      const networkMinGap = 2 * Math.max(1, gridColumns)
      for (const c of candidates) {
        if (c.priority !== 5) continue
        let blocked = false
        for (const k of keptByIndex.keys()) {
          if (Math.abs(k - c.postIndex) < networkMinGap) { blocked = true; break }
        }
        if (!blocked) keptByIndex.set(c.postIndex, c)
      }

      // --- Stable placement ---
      // The pass above recomputes every slot from the whole post list, so each time a page was appended
      // a slot could appear, vanish or jump (measured: house ad at post 27 with 70 posts, at post 9 with
      // 104; a network ad at post 1 that disappeared), pushing the cards under it around. Placement is
      // now append-only: a slot already placed stays on its post, and only slots far enough down
      // (past ~4 rows / LATE_SLOT_MIN_INDEX posts, normally below the fold) or on newly loaded posts may be added.
      // The creative inside a kept slot is still refreshed from the current candidates.
      const sig = [isAlbumView, adTestMode, effectivePremiumUser, showAdsEnabled, showScriptPromosEnabled, showCommissionPromosEnabled, gridColumns, posts[0]?.site, posts[0]?.id].join("|")
      const memo = stableSlotsRef.current
      if (memo.sig !== sig || posts.length < memo.len) { memo.sig = sig; memo.len = 0; memo.kept = new Map() }
      const baseLen = memo.len
      const lateFloor = Math.max(LATE_SLOT_MIN_INDEX, Math.max(1, gridColumns) * 4)
      const nextKept = new Map<number, AdCandidate>()
      const candidateFor = new Map<string, AdCandidate>()
      for (const c of candidates) candidateFor.set(`${c.postIndex}|${c.item.type}`, c)
      const isDismissed = (item: AdItem) =>
        (item.type === "ad" && dismissedAds.has(item.ad.id)) ||
        (item.type === "script_promo" && dismissedPromos.has(item.promo.campaign_id)) ||
        (item.type === "commission_promo" && dismissedCommissionPromos.has(item.promo.promotion_id))
      for (const [index, old] of memo.kept) {
        if (index >= posts.length || isDismissed(old.item)) continue
        const fresh = candidateFor.get(`${index}|${old.item.type}`)
        nextKept.set(index, fresh && !isDismissed(fresh.item) ? fresh : old)
      }
      const nearKept = (index: number, min: number, networkOnly: boolean) => {
        for (const [k, c] of nextKept) {
          if (networkOnly && c.priority === 5) continue
          if (Math.abs(k - index) < min) return true
        }
        return false
      }
      for (const [index, c] of [...keptByIndex.entries()].sort((a, b) => a[0] - b[0])) {
        if (nextKept.has(index) || (index < baseLen && index < lateFloor)) continue
        const isNetwork = c.priority === 5
        if (nearKept(index, isNetwork ? networkMinGap : effectiveMinGap, !isNetwork)) continue
        nextKept.set(index, c)
      }
      memo.len = posts.length
      memo.kept = nextKept

      const result: typeof items = []
      posts.forEach((post, index) => {
        result.push({ type: "post", post })
        const kept = nextKept.get(index)
        if (kept) result.push(kept.item)
      })
      items = result
    }

    // Reorder items based on grid flow direction
    // Reorder items based on grid flow direction AND layout type
    // Masonry layout uses CSS columns which naturally flow top-to-bottom
    // Regular grid layout naturally flows left-to-right
    // Masonry no longer needs (or wants) this: it distributes items into explicit columns itself, shortest first,
    // which already reads left-to-right in feed order. The old transpose was written for CSS multi-column
    // masonry; with explicit columns it re-mapped EVERY item whenever the item count changed, so each appended
    // page reshuffled the whole grid (measured: layout shift 0.4-0.6 per page on Sakugabooru, 0 with it off).
    const needsReordering = gridLayout === "masonry"
      ? false
      : gridFlowDirection === "horizontal" // Grid: reorder when user wants vertical (stored as "horizontal")
    
    if (needsReordering) {
      // Reorder items to transpose the grid
      const reordered: typeof items = []
      const totalItems = items.length
      const rows = Math.ceil(totalItems / gridColumns)
      
      // Fill column by column instead of row by row
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < gridColumns; col++) {
          const index = col * rows + row
          if (index < totalItems) {
            reordered.push(items[index])
          }
        }
      }
      
      return reordered
    }
    
    // No reordering needed - return items as-is
    return items
  }, [
    posts,
    showAdsEnabled,
    effectivePremiumUser,
    dynamicAdFrequency,
    dynamicAdMaxSlots,
    customAds,
    liveHouseAds,
    liveAffiliateAds,
    dismissedAds,
    showScriptPromosEnabled,
    showCommissionPromosEnabled,
    scriptPromos,
    dismissedPromos,
    dismissedCommissionPromos,
    dynamicScriptPromoFrequency,
    gridFlowDirection,
    gridLayout,
    gridColumns,
    networkAdPool,
    networkPoolState,
    outstreamVideoJs,
    outstreamZoneId,
  ])

  // Group flat item list into rows for virtual grid rendering.
  // Masonry uses CSS columns (browser-controlled heights), so skip it there.
  const virtualRows = useMemo(() => {
    if (gridLayout === "masonry") return [] as (typeof postsWithAdsAndPromos)[]
    const rows: (typeof postsWithAdsAndPromos)[] = []
    for (let i = 0; i < postsWithAdsAndPromos.length; i += gridColumns) {
      rows.push(postsWithAdsAndPromos.slice(i, i + gridColumns))
    }
    return rows
  }, [postsWithAdsAndPromos, gridColumns, gridLayout])

  // Row virtualizer — only active when a scroll container ref is provided.
  // count=0 when masonry (virtualRows is empty), so this is a no-op there.
  const rowVirtualizer = useVirtualizer({
    count: virtualRows.length,
    getScrollElement: () => scrollContainerRef?.current ?? null,
    estimateSize: () => 300,
    overscan: 5,
    measureElement: (el) => el.getBoundingClientRect().height,
  })

  const handleDismissAd = (adId: string) => {
    setDismissedAds((prev) => new Set([...prev, adId]))
  }

  const handleDismissPromo = (campaignId: string) => {
    setDismissedPromos((prev) => new Set([...prev, campaignId]))
  }

  const handleDismissCommissionPromo = (promotionId: string) => {
    setDismissedCommissionPromos((prev) => new Set([...prev, promotionId]))
  }

  // True only while the user is picking content for a specific Gooncave slot — used to
  // highlight the "+" as the intended action and to make it fill that slot on click.
  const isPickingForGooncaveSlot = useScriptLinkingStore(
    (s) => s.isLinkingMode && s.returnToTab === "Gooncave",
  )

  const handleFavorite = (post: BooruPost, e: React.MouseEvent) => {
    e.stopPropagation()
    toggleFavorite(post)
  }

  const handleQueueGooncave = (post: BooruPost, e: React.MouseEvent) => {
    e.stopPropagation()
    // Picking for a specific Gooncave slot (the user pressed "+" on an empty cell and got
    // sent here): fill that slot and go back, rather than appending to the end of the queue
    // where they'd never find it.
    if (isPickingForGooncaveSlot) {
      useScriptLinkingStore.setState({ pendingPost: post, returnToEditor: true, isLinkingMode: false })
      return
    }
    const { queue, addToQueue } = useGooncaveStore.getState()
    const alreadyQueued = queue.some((qp) => qp.id === post.id && qp.site === post.site)
    if (alreadyQueued) {
      toast.info(t("alreadyInGooncaveQueue"))
      return
    }
    addToQueue(post)
    toast.success(t("addedToGooncaveQueue"))
  }

  const handleMouseDown = (post: BooruPost) => {
    setLongPressTriggered(false)
    const timer = setTimeout(() => {
      // Check if this content type should show the collection modal
      if (contentTypeDetector.shouldShowCollectionModal(post)) {
        setCollectionPost(post)
        setLongPressTriggered(true)
      } else {
        console.log('[BooruGrid] Collection modal suppressed for category/sub-category')
      }
    }, 500)
    setLongPressTimer(timer)
  }

  const handleMouseUp = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer)
      setLongPressTimer(null)
    }
  }

  const handleMouseLeave = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer)
      setLongPressTimer(null)
    }
  }

  const handleClick = (post: BooruPost) => {
    if (longPressTriggered) {
      setTimeout(() => setLongPressTriggered(false), 100)
      return
    }
    onPostClick?.(post)
  }

  const handleTagClick = (tag: string, e: React.MouseEvent) => {
    e.stopPropagation()
    onTagClick?.(tag)
  }

  const handleTagsSelected = (includeTags: string[], excludeTags: string[]) => {
    // Combine include and exclude tags with proper formatting
    const searchTags = [
      ...includeTags,
      ...excludeTags.map(tag => `-${tag}`)
    ]
    
    // Trigger search with combined tags
    if (searchTags.length > 0 && onTagClick) {
      // Join tags with spaces for the search query
      onTagClick(searchTags.join(' '))
    }
  }

  const getAlbumPageCount = (post: BooruPost) => {
    if (post.albumImages && post.albumImages.length > 1) {
      return post.albumImages.length
    }
    if (post.albumMediaCount && post.albumMediaCount > 1) {
      return post.albumMediaCount
    }
    // Lazy-load placeholders (e.g. Erome posts past the eager-fetch limit) are
    // typed "album" with a galleryUrl but haven't been fetched yet, so the real
    // item count is unknown. Signal -1 ("likely multiple, count unknown") so the
    // grid badge matches what the modal will actually reveal on click, instead
    // of silently looking like a plain single image.
    if (post.type === "album" && post.rawPost?.needsDetailFetch && (!post.albumImages || post.albumImages.length === 0)) {
      return -1
    }
    return 0
  }

  const getPlayableVideoSrc = (post: BooruPost) => {
    const candidates = [post.sampleUrl, post.fileUrl]
    return candidates.find((url): url is string => !!url && inferOrderMediaType(url, post.type) === "video")
  }

  const buildPostCardProps = (post: BooruPost) => {
    const postKey = `${post.site}-${post.id}`
    const isFav = isFavorite(postKey)
    const bearerToken = post.site === 'sankaku' ? sankakuToken : post.site === 'idolsankaku' ? idolSankakuToken : undefined
    const playableVideoSrc = getPlayableVideoSrc(post)
    const hasGifTag = post.rawPost?.hasGifTag
    // rawPost.hasVideo is a lazy-placeholder-only signal (currently erome —
    // see parseEromeHtml): fileUrl/sampleUrl are just the thumbnail JPG until
    // the modal resolves the real content on click, so playableVideoSrc can't
    // detect it. The listing page marks video albums separately from image
    // albums, and that survives onto the placeholder even though the actual
    // video URL doesn't.
    const isVideoPost = !!playableVideoSrc || !!post.rawPost?.embedUrl || !!post.rawPost?.hasVideo
    const isVRVideo = isVideoPost && isVRPost(post)
    // Masonry needs each tile's real aspect ratio to pack columns properly.
    // Many booru sources (Gelbooru/Danbooru-style) generate "preview" thumbs
    // as fixed, center-cropped squares — real aspect ratio only survives in
    // the "sample" quality image. Square mode keeps "preview" since it's
    // smaller/faster and squares are the intended look there anyway.
    const imageSrc = getBestImageUrl(post, bearerToken, gridLayout === "masonry" || gridLayout === "fitted" ? "sample" : "preview")
    const shouldFetchDoujinsMetadata = post.site === 'doujins' &&
      post.rawPost?.galleryUrl &&
      !(post.rawPost.galleryUrl.startsWith('category:') && !post.rawPost.galleryUrl.includes('/'))
    const doujinsMetadata = shouldFetchDoujinsMetadata ? getMetadata(postKey) : null
    const isLazyEromePlaceholder =
      post.site === 'erome' && post.type === 'album' && post.rawPost?.needsDetailFetch &&
      (!post.albumImages || post.albumImages.length === 0)
    const eromeMetadata = isLazyEromePlaceholder ? getEromeMetadata(postKey) : null
    const albumPageCount = doujinsMetadata?.pageCount || eromeMetadata?.pageCount || getAlbumPageCount(post)
    const postIsAlbum = albumPageCount > 1 || albumPageCount === -1
    const showPremiumBadge = !!(post.isPremium || (post.site === 'doujins' && doujinsMetadata?.pageCount === 1))
    const isDirectVideoThumb = /\.(webm|mp4|mov|avi|mkv|flv|wmv|m4v|3gp|ogv)(\?|$)/i.test(imageSrc)
    const scrubVideoSrc = isVideoPost && !isDirectVideoThumb ? playableVideoSrc : undefined

    return {
      postKey,
      isFav,
      hasGifTag,
      isVideoPost,
      isVRVideo,
      imageSrc,
      doujinsMetadata,
      albumPageCount,
      postIsAlbum,
      showPremiumBadge,
      scrubVideoSrc,
    }
  }

  const renderTickers = (post: BooruPost, doujinsMetadata: ReturnType<typeof getMetadata>) => {
    const postKey = `${post.site}-${post.id}`
    return (
      <>
        {post.site === 'hitomi' && post.tags && post.tags.length > 0 && !isAlbumView && (
          <div className="mt-1">
            <HitomiTagTicker tags={post.tags} onTagsSelected={handleTagsSelected} />
          </div>
        )}
        {(() => {
          const isDoujins = post.site === 'doujins'
          const isXlecx = post.site === 'xlecx'
          const hasTitle = !!post.title
          const notInAlbum = !isAlbumView
          const galleryUrl = post.rawPost?.galleryUrl || ''
          const isMainCategory = galleryUrl.startsWith('category:') && !galleryUrl.includes('/')
          const isSeriesPage = galleryUrl.startsWith('category:series/')
          let shouldShow = false
          if (isDoujins) shouldShow = hasTitle && notInAlbum && !isMainCategory
          else if (isXlecx) shouldShow = hasTitle && notInAlbum
          if (!shouldShow) return null
          return (
            <div className="mt-1">
              {isSeriesPage ? (
                <TitleTicker title={post.title ?? ""} />
              ) : (
                <CombinedInfoTicker
                  title={post.title ?? ""}
                  tags={doujinsMetadata?.tags || post.tags || []}
                  onTagsSelected={handleTagsSelected}
                />
              )}
            </div>
          )
        })()}
      </>
    )
  }

  const renderPostItem = (post: BooruPost) => {
    if (post.type === "audio") {
      return (
        <AudioCard
          key={`${post.site}-${post.id}`}
          post={post}
          onTagClick={onTagClick}
          className="w-full"
        />
      )
    }

    const postKey = `${post.site}-${post.id}`
    const { isFav, hasGifTag, isVideoPost, isVRVideo, imageSrc, doujinsMetadata, albumPageCount, postIsAlbum, showPremiumBadge, scrubVideoSrc } = buildPostCardProps(post)
    const isHitomiAlbum = post.site === 'hitomi' && postIsAlbum

    return (
      <div key={postKey} className="flex flex-col">
        <BooruPostCard
          post={post}
          fitted={fitted}
          imageSrc={imageSrc}
          scrubVideoSrc={scrubVideoSrc}
          borderRadius={borderRadius}
          isFav={isFav}
          isVideoPost={isVideoPost}
          isVRVideo={isVRVideo}
          hasGifTag={hasGifTag}
          postIsAlbum={postIsAlbum}
          albumPageCount={albumPageCount}
          showPremiumBadge={showPremiumBadge}
          scriptCount={getScriptCount(post)}
          topScript={getTopScript(post)}
          onClick={() => handleClick(post)}
          onFavorite={(e) => handleFavorite(post, e)}
          onQueueGooncave={(e) => handleQueueGooncave(post, e)}
          highlightQueueGooncave={isPickingForGooncaveSlot}
          onMouseDown={() => handleMouseDown(post)}
          onMouseUp={handleMouseUp}
          onMouseLeaveOuter={handleMouseLeave}
          onPremiumClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            setShowPremiumModal(true)
          }}
          observeRef={(el) => {
            const shouldObserveDoujins = el && post.site === 'doujins' &&
              post.rawPost?.galleryUrl &&
              !(post.rawPost.galleryUrl.startsWith('category:') && !post.rawPost.galleryUrl.includes('/'))
            if (shouldObserveDoujins) {
              observePost(el, postKey, post.rawPost.galleryUrl)
              return
            }
            const shouldObserveErome = el && post.site === 'erome' && post.type === 'album' &&
              post.rawPost?.needsDetailFetch && (!post.albumImages || post.albumImages.length === 0) && post.source
            if (shouldObserveErome) observeErome(el, postKey, post.source!)
          }}
        />
        {/* Doujins audio badge — outside the card (not a video, so no scrub conflict) */}
        {post.site === 'doujins' && post.rawPost?.hasAudio && !isAlbumView && (
          <div className="absolute top-2 right-2 flex items-center gap-1 bg-green-500/90 backdrop-blur-sm text-white px-2 py-1 rounded-md shadow-lg pointer-events-none">
            <Volume2 className="h-3 w-3" />
            <span className="text-xs font-medium">{t("audio")}</span>
          </div>
        )}
        {renderTickers(post, doujinsMetadata)}
      </div>
    )
  }

  // Square grid mode gives every post a uniform 1:1 tile (see BooruPostCard);
  // ad/promo cards need the same shape there or they visibly break the grid
  // with a different height than their neighbors. Masonry keeps portrait,
  // which matches its own natural-aspect-ratio post tiles better.
  const adAspectRatio = gridLayout === "square" ? "square" : gridLayout === "fitted" ? { landscape: "landscape", portrait: "portrait", square: "square" }[gridFitShape] as "square" | "portrait" | "landscape" : "portrait"

  const renderScriptPromo = (promo: ScriptPromoAd, index: number) => (
    <ScriptPromoCard
      key={`promo-${promo.campaign_id}-${index}`}
      promo={promo}
      onTryNow={(p) => setTrialPromo(p)}
      onBuyNow={(p) => setPurchasePromo(p)}
      onDismiss={() => handleDismissPromo(promo.campaign_id)}
      aspectRatio={adAspectRatio}
    />
  )

  const renderCommissionPromo = (promo: CommissionPromoAd, index: number) => (
    <CommissionPromoCard
      key={`commission-promo-${promo.promotion_id}-${index}`}
      promo={promo}
      onDismiss={() => handleDismissCommissionPromo(promo.promotion_id)}
      aspectRatio={adAspectRatio}
    />
  )

  // Single-item renderer shared between virtual grid rows and the flat fallback
  const renderSingleItem = (item: typeof postsWithAdsAndPromos[number], globalIndex: number) => {
    if (item.type === "script_promo") return renderScriptPromo(item.promo, globalIndex)
    if (item.type === "commission_promo") return renderCommissionPromo(item.promo, globalIndex)
    if ("ad" in item && item.type === "ad")
      return (
        <RotatingInlineGridAd
          key={`ad-${item.ad.id}-${globalIndex}`}
          initialAd={item.ad}
          pool={item.pool}
          cycleSeconds={dynamicAdCycleSeconds}
          aspectRatio={adAspectRatio}
          fixedShape={gridLayout === "fitted"}
          onDismiss={() => handleDismissAd(item.ad.id)}
        />
      )
    if ("type" in item && item.type === "video_ad")
      return <InFeedVideoAd key={`vad-${globalIndex}`} ad={(item as any).ad} className="aspect-[9/16]" />
    if ("type" in item && item.type === "network_ad")
      return !(item as any).embedJs ? (
        <AdSlotPlaceholder key={`nad-${globalIndex}`} width={(item as any).zoneWidth} height={(item as any).zoneHeight} />
      ) : (
        <NetworkDisplayAd
          key={`nad-${globalIndex}`}
          embedJs={(item as any).embedJs}
          label={(item as any).label}
          zoneId={(item as any).zoneId}
          zoneWidth={(item as any).zoneWidth}
          zoneHeight={(item as any).zoneHeight}
        />
      )
    const post = (item as { type: "post"; post: BooruPost }).post
    return renderPostItem(post)
  }

  const sharedModals = (
    <>
      {collectionPost && (
        <SaveToGoonfolioDialog
          open={!!collectionPost}
          onOpenChange={(open) => !open && setCollectionPost(null)}
          item={{ itemType: "external_content", externalContent: collectionPost }}
        />
      )}
      <DoujinsPremiumModal isOpen={showPremiumModal} onClose={() => setShowPremiumModal(false)} />
      <ScriptTrialModal
        promo={trialPromo}
        onClose={() => setTrialPromo(null)}
        onPurchase={(promo) => {
          setTrialPromo(null)
          setPurchasePromo(promo)
        }}
      />
      <ScriptPurchaseModal promo={purchasePromo} onClose={() => setPurchasePromo(null)} />
    </>
  )

  if (gridLayout === "masonry") {
    // ── Why this distributes items into explicit columns instead of using
    //    CSS multi-column (`columns` / `column-count`) ────────────────────
    //
    // CSS multi-column defaults to `column-fill: balance`, which recomputes
    // the ideal per-column height across ALL of a container's children every
    // time content is appended — so every infinite-scroll page load visibly
    // shifted posts the user had already scrolled past. Measured live: 8
    // tracked images moved by inconsistent amounts (3284/3284/3284/3284/
    // 2903/2903/2705/2142px), which is reflow, not a uniform scroll offset.
    //
    // The first attempt at fixing that split the feed into one `columns`
    // container per loaded page, so balancing stayed scoped to a page. That
    // did stop the reflow, but each container's columns end at different
    // heights and the next container starts below the TALLEST one — putting
    // a ragged hole at every page seam. Measured live: ~150px of dead space
    // per seam, ~455px across three pages and growing with every load. That
    // was reported (with screenshots) as the "empty spaces" problem.
    //
    // Distributing into explicit flex columns ourselves fixes both at once:
    // an item's column assignment depends only on the items BEFORE it, so
    // appending can never move anything already placed (no reflow), and
    // there is exactly one continuous container (no seams). This is the
    // standard masonry approach and it makes the per-page batching above
    // unnecessary, which is why `infiniteScrollBatchBoundaries` is no longer
    // read here.
    //
    // Balancing uses each item's ESTIMATED height rather than a measured
    // one: measuring the DOM would reintroduce the same
    // append-changes-earlier-layout coupling this is meant to remove, and
    // estimates are available synchronously from data we already have.
    const columnCount = Math.max(1, gridColumns)
    const columns: { item: typeof postsWithAdsAndPromos[number]; index: number }[][] =
      Array.from({ length: columnCount }, () => [])
    const columnHeights = new Array(columnCount).fill(0)

    // Relative units — only the ratio between items matters, so this works
    // without knowing the real rendered column width.
    const DEFAULT_ASPECT = 1.4 // portrait-ish, matches the typical card
    const estimateAspect = (entry: typeof postsWithAdsAndPromos[number]): number => {
      if (entry.type === "post") {
        const p = entry.post
        // Many sources (erome placeholders especially) report 0x0 until the
        // real media resolves — fall back rather than dividing by zero.
        if (p.width && p.height && p.width > 0) return p.height / p.width
        return DEFAULT_ASPECT
      }
      if (entry.type === "network_ad") {
        const w = (entry as any).zoneWidth
        const h = (entry as any).zoneHeight
        if (w && h && w > 0) return h / w
      }
      // Rendered at a hardcoded aspect-[9/16] (see renderMasonryItem below) —
      // using DEFAULT_ASPECT here understated it by ~27%, which let the
      // column holding a video ad get overstuffed relative to its real
      // height and fall behind its neighbors, producing a visible gap.
      if (entry.type === "video_ad") return 16 / 9
      return DEFAULT_ASPECT
    }

    // A column assignment, once made, is REMEMBERED against the item's own
    // stable identity and never recomputed. Shortest-first alone is not
    // enough to keep the layout still, because its inputs mutate underneath
    // it: an Erome placeholder reports 0x0 until its real media resolves,
    // and the grid ad pool arrives from an async fetch. Either one changes
    // an item's estimated height after the fact, which shifts the running
    // column totals and therefore reassigns every item that came after it.
    // Measured live with exactly that bug present: the first four tracked
    // cards moved by a uniform 4054px (just the scroll), while the fifth and
    // sixth moved 4147px and 4311px — i.e. genuinely relocated, not scrolled.
    // Pinning the assignment makes late-arriving data unable to disturb
    // anything already on screen.
    if (masonryColumnCountRef.current !== columnCount) {
      // Column count changed (viewport resize, settings change) — every old
      // assignment refers to a column layout that no longer exists, so start
      // over. This is a deliberate, user-visible relayout, not a silent one.
      masonryAssignmentsRef.current.clear()
      masonryColumnCountRef.current = columnCount
    }

    // Stable per-item key. Posts have a real identity; ads/promos don't, so
    // they're keyed by their ordinal among their own type, which keeps them
    // pinned even as surrounding indices shift when the ad pool resolves.
    const typeOrdinals = new Map<string, number>()
    const stableKeyFor = (entry: typeof postsWithAdsAndPromos[number]): string => {
      if (entry.type === "post") return `post:${entry.post.site}-${entry.post.id}`
      if (entry.type === "script_promo") return `script_promo:${entry.promo.campaign_id}`
      if (entry.type === "commission_promo") return `commission_promo:${entry.promo.promotion_id}`
      const n = typeOrdinals.get(entry.type) ?? 0
      typeOrdinals.set(entry.type, n + 1)
      return `${entry.type}:#${n}`
    }

    postsWithAdsAndPromos.forEach((item, index) => {
      const key = stableKeyFor(item)
      let target = masonryAssignmentsRef.current.get(key)
      if (target === undefined || target >= columnCount) {
        // First time seeing this item — place it in whichever column is
        // currently shortest. Ties resolve leftmost so the result is
        // deterministic. Recording it means this decision is final.
        target = 0
        for (let c = 1; c < columnCount; c++) {
          if (columnHeights[c] < columnHeights[target]) target = c
        }
        masonryAssignmentsRef.current.set(key, target)
      }
      columns[target].push({ item, index })
      columnHeights[target] += estimateAspect(item)
    })

    const renderMasonryItem = (item: typeof postsWithAdsAndPromos[number], index: number) => {
            if (item.type === "script_promo") {
              return (
                <div
                  key={`promo-${item.promo.campaign_id}-${index}`}
                  className="inline-block w-full"
                  
                >
                  {renderScriptPromo(item.promo, index)}
                </div>
              )
            }
            if (item.type === "commission_promo") {
              return (
                <div
                  key={`commission-promo-${item.promo.promotion_id}-${index}`}
                  className="inline-block w-full"
                  
                >
                  {renderCommissionPromo(item.promo, index)}
                </div>
              )
            }
            if ("ad" in item && item.type === "ad") {
              return (
                <div
                  key={`ad-${item.ad.id}-${index}`}
                  className="inline-block w-full"
                  
                >
                  <RotatingInlineGridAd
                    initialAd={item.ad}
                    pool={item.pool}
                    cycleSeconds={dynamicAdCycleSeconds}
                    aspectRatio="portrait"
                    onDismiss={() => handleDismissAd(item.ad.id)}
                  />
                </div>
              )
            }
            if ("type" in item && item.type === "video_ad") {
              return (
                <div key={`vad-${index}`} className="inline-block w-full" >
                  <InFeedVideoAd ad={(item as any).ad} className="aspect-[9/16]" />
                </div>
              )
            }
            if ("type" in item && item.type === "network_ad") {
              return (
                <div key={`nad-${index}`} className="inline-block w-full" >
                  {!(item as any).embedJs ? (
                    <AdSlotPlaceholder width={(item as any).zoneWidth} height={(item as any).zoneHeight} />
                  ) : (
                    <NetworkDisplayAd
                      embedJs={(item as any).embedJs}
                      label={(item as any).label}
                      zoneId={(item as any).zoneId}
                      zoneWidth={(item as any).zoneWidth}
                      zoneHeight={(item as any).zoneHeight}
                    />
                  )}
                </div>
              )
            }
            const post = (item as { type: "post"; post: BooruPost }).post
            const postKey = `${post.site}-${post.id}`
            if (post.type === "audio") {
              return (
                <div key={`audio-${post.id}`} className="inline-block w-full" >
                  <AudioCard post={post} onTagClick={onTagClick} />
                </div>
              )
            }
            const { isFav, hasGifTag, isVideoPost, isVRVideo, imageSrc, doujinsMetadata, albumPageCount, postIsAlbum, showPremiumBadge, scrubVideoSrc } = buildPostCardProps(post)
            return (
              <div key={postKey} className="inline-block w-full flex flex-col" >
                <BooruPostCard
                  post={post}
                  imageSrc={imageSrc}
                  scrubVideoSrc={scrubVideoSrc}
                  borderRadius={borderRadius}
                  isFav={isFav}
                  isVideoPost={isVideoPost}
                  isVRVideo={isVRVideo}
                  hasGifTag={hasGifTag}
                  // Masonry needs each tile's REAL height to pack columns
                  // correctly — a post with no known dimensions should size
                  // itself from the actual loaded image instead of forcing
                  // a square, which is what was flattening masonry into a
                  // uniform square grid.
                  useNaturalSizeFallback
                  postIsAlbum={postIsAlbum}
                  albumPageCount={albumPageCount}
                  showPremiumBadge={showPremiumBadge}
                  scriptCount={getScriptCount(post)}
                  topScript={getTopScript(post)}
                  onClick={() => handleClick(post)}
                  onFavorite={(e) => handleFavorite(post, e)}
                  onQueueGooncave={(e) => handleQueueGooncave(post, e)}
                  highlightQueueGooncave={isPickingForGooncaveSlot}
                  onMouseDown={() => handleMouseDown(post)}
                  onMouseUp={handleMouseUp}
                  onMouseLeaveOuter={handleMouseLeave}
                  onPremiumClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    setShowPremiumModal(true)
                  }}
                  observeRef={(el) => {
                    const shouldObserveDoujins = el && post.site === 'doujins' &&
                      post.rawPost?.galleryUrl &&
                      !(post.rawPost.galleryUrl.startsWith('category:') && !post.rawPost.galleryUrl.includes('/'))
                    if (shouldObserveDoujins) {
                      observePost(el, postKey, post.rawPost.galleryUrl)
                      return
                    }
                    const shouldObserveErome = el && post.site === 'erome' && post.type === 'album' &&
                      post.rawPost?.needsDetailFetch && (!post.albumImages || post.albumImages.length === 0) && post.source
                    if (shouldObserveErome) observeErome(el, postKey, post.source!)
                  }}
                />
                {renderTickers(post, doujinsMetadata)}
              </div>
            )
    }

    return (
      <>
        <div
          className={cn("leading-none py-0 leading-[0] px-1 flex items-start", className)}
          style={{ gap: `${gridGap}px` }}
        >
          {columns.map((column, columnIndex) => (
            <div
              key={`masonry-col-${columnIndex}`}
              className="flex flex-col min-w-0 flex-1"
              style={{ gap: `${gridGap}px` }}
            >
              {column.map(({ item, index }) => renderMasonryItem(item, index))}
            </div>
          ))}
        </div>
        {sharedModals}
      </>
    )
  }

  // Explicit column template — virtual grid needs a fixed column count so our row
  // grouping matches what actually renders. auto-fit is only used in the fallback path.
  const gridColTemplate = isShowingCategories
    ? "repeat(3, minmax(0, 1fr))"
    : `repeat(${gridColumns}, minmax(0, 1fr))`

  // Virtualized path — only active when a scroll container ref was provided
  if (scrollContainerRef) {
    return (
      <>
        <div
          className={cn("mx-auto w-full relative", className)}
          style={{ maxWidth: isShowingCategories ? "1400px" : isAudioGrid ? "960px" : "100%" }}
        >
          {/* top padding */}
          <div style={{ height: "16px" }} />
          {/* virtual scroll host — height = sum of all measured row heights */}
          <div style={{ position: "relative", height: `${rowVirtualizer.getTotalSize()}px` }}>
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const rowItems = virtualRows[virtualRow.index]
              const baseIndex = virtualRow.index * gridColumns
              return (
                <div
                  key={virtualRow.key}
                  data-index={virtualRow.index}
                  ref={rowVirtualizer.measureElement}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: "16px",
                    right: "16px",
                    transform: `translateY(${virtualRow.start}px)`,
                    paddingBottom: `${gridGap}px`,
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: gridColTemplate,
                      gap: `${gridGap}px`,
                      ...(isAudioGrid ? { maxWidth: "960px", margin: "0 auto" } : {}),
                    }}
                  >
                    {rowItems.map((item, colIndex) =>
                      renderSingleItem(item, baseIndex + colIndex),
                    )}
                  </div>
                </div>
              )
            })}
          </div>
          {/* bottom padding */}
          <div style={{ height: "16px" }} />
        </div>
        {sharedModals}
      </>
    )
  }

  // Non-virtual fallback (used when no scroll container ref is passed, e.g. album views)
  const getGridColsClass = () => {
    if (isShowingCategories) return { gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }
    if (isAudioGrid) return { gridTemplateColumns: "minmax(0, 1fr)" }
    if (gridColumnsAuto && !hasGridColumnsOverride) return { gridTemplateColumns: `repeat(${gridColumns}, minmax(0, 1fr))` }
    const minWidth = Math.max(200, Math.floor(1400 / gridColumns))
    return { gridTemplateColumns: `repeat(auto-fit, minmax(${minWidth}px, 1fr))` }
  }

  return (
    <>
      <div
        className={cn("grid p-4 mx-auto w-full", className)}
        style={{ gap: `${gridGap}px`, ...getGridColsClass(), maxWidth: isShowingCategories ? "1400px" : "100%" }}
      >
        {postsWithAdsAndPromos.map((item, index) => renderSingleItem(item, index))}
      </div>
      {sharedModals}
    </>
  )
}
